import React from 'react'

import get from 'lodash/get'
import isNil from 'lodash/isNil'
import isEmpty from 'lodash/isEmpty'
import defaultTo from 'lodash/defaultTo'

import TableData, { ITableData } from 'components/layout/table/tableData'
import Input from 'components/ui/input'
import Select, { IOption } from 'components/ui/select'

export type IAlternative = {
  articleNumber: string
  product: string
  unitPrice: number
}

export interface IItem {
  value: string | number
  unit?: string
  editData?: {
    value: string | number
    unit?: string
  }
}

interface IEditableTableData extends ITableData {
  articleNumber: string
  productName?: string
  item: IItem
  alternatives?: IAlternative[]
  isLoading?: boolean
  placeholder?: string
  unit?: string
  onChange?: (key: string, item: IItem) => void
  onOptionChange?: (alternative: IAlternative) => void
}

const EditableTableData: React.FC<IEditableTableData> = props => {
  const {
    articleNumber,
    productName,
    name,
    col,
    item,
    unit,
    alternatives,
    isLoading,
    leftAlign,
    placeholder,
    onChange,
    onOptionChange,
  } = props

  const hasAlternatives = () =>
    !isNil(alternatives) && !isEmpty(alternatives) && !isNil(productName)

  const getEditValue = () => {
    const { item } = props
    return get(item, `editData.value`, undefined)
  }

  const getItemValue = () => {
    const { item } = props
    return defaultTo(get(item, `value`, undefined), item)
  }

  const getValue = defaultTo(getEditValue(), getItemValue())

  const hasUnit = () => (props.unit ? true : false)

  const handleChange = (key: string, item: IItem) => {
    if (onChange) onChange(key, item)
  }

  const handleOptionChange = (option: IOption) => {
    const { value: selectedArticleNumber } = option

    const selectedAlternative = defaultTo(alternatives, []).find(
      (alternative: IAlternative) => alternative.articleNumber === selectedArticleNumber,
    )

    if (!isNil(onOptionChange) && !isNil(selectedAlternative) && selectedAlternative) {
      onOptionChange(selectedAlternative)
    }
  }

  if (hasAlternatives()) {
    const aternativesOptions: IOption[] = (alternatives || []).map((alternative: IAlternative) => ({
      value: alternative.articleNumber,
      label: alternative.product,
    }))

    return (
      <div key={name} className={`flex w-${col}/12 pl-30 pt-10 first-child:pl-0`}>
        <div className={`flex ${hasUnit() ? 'w-8/12' : 'w-full'}`}>
          <Select
            defaultValue={articleNumber}
            options={aternativesOptions}
            onChange={handleOptionChange}
            placeholder={placeholder}
            isLoading={isLoading}
            underline
            menuWidth={450}
          />
        </div>
      </div>
    )
  } else if (!isNil(alternatives) && isEmpty(alternatives) && !isNil(productName)) {
    return (
      <TableData
        key={name}
        name={name}
        className="pl-30 first-child:pl-0"
        col={col}
        text={productName}
      />
    )
  }

  return (
    <div key={name} className={`flex items-end w-${col}/12 pl-30 pt-10 first-child:pl-0`}>
      <div className={`flex ${hasUnit() ? 'w-8/12' : 'w-full'}`}>
        <Input
          type={hasUnit() ? 'number' : 'text'}
          unit={hasUnit() ? unit : undefined}
          value={hasUnit() ? Number(getValue) : String(getValue)}
          name={name}
          onChange={value => {
            // If the item has editData defined,
            // use and manipulate this data instead
            if (!isNil(item.editData)) {
              handleChange(
                name,
                Object.assign({ value: item.value }, hasUnit() ? { unit: item.unit } : null, {
                  editData: {
                    ...item.editData,
                    value: hasUnit() && value ? Number(value) : value,
                  },
                }),
              )
            } else {
              handleChange(
                name,
                Object.assign(
                  { value: hasUnit() && value ? Number(value) : value },
                  hasUnit() ? { unit: item.unit } : null,
                ),
              )
            }
          }}
          placeholder={placeholder}
          underline
          leftAlign={leftAlign}
          rightAlign={isNil(leftAlign)}
        />
      </div>
      {hasUnit() && (
        <div className="flex w-4/12 ml-15">
          <TableData name={`${name}-unit`} col={12} text={unit} leftAlign={false} />
        </div>
      )}
    </div>
  )
}

export default EditableTableData
